import { useMutation, useQuery } from '@apollo/client';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload as ManagedUpload } from '@aws-sdk/lib-storage';
import { Add, CheckCircle, DriveFolderUpload, Movie, PictureAsPdf } from '@mui/icons-material';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  styled,
  type LinearProgressProps,
} from '@mui/material';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type Dispatch,
  type Ref,
} from 'react';
import type { DropEvent, DropzoneOptions, FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { Pptx } from '~/components/icons';
import { useAppContext } from '~/contexts';
import { useUploadDispatch, useUploadState } from '~/contexts/upload';
import { CreateUploadConfigDocument } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { assert, exhausted } from '~/lib/assert';
import { formatBytes } from '~/lib/units';
import { UploadDialogDocument } from './UploadDialog.generated';

export type UploadState = 'pending' | 'finished' | 'canceled';

// PowerPoint decks are accepted and converted server-side to an mp4 slideshow.
export const PPT_MIME_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const UPLOAD_DROPZONE_OPTIONS = {
  accept: ['image/*', 'video/*', 'application/pdf', ...PPT_MIME_TYPES],
  maxSize: 2 ** 30, // 1 GiB
  minSize: 1,
} satisfies DropzoneOptions;

interface Upload {
  readonly file: File;
  readonly key: string;
  readonly manager: ManagedUpload;
  readonly previewUrl: string | undefined;
  readonly state: UploadState;
}

type UploadsAction = Readonly<
  | { type: 'reset' }
  | { type: 'add'; uploads: Upload[] }
  | { type: 'updateState'; key: string; state: UploadState }
>;

const ProgressBar = styled(LinearProgress)({
  backgroundColor: 'rgba(230, 230, 230, 0.5)',
  borderRadius: '0.3rem',
  height: '0.4rem',
  width: '100%',
});

const Progress = (props: LinearProgressProps) => <ProgressBar variant="determinate" {...props} />;

// --- File row component ---

interface FileRowProps {
  readonly upload: Upload;
  readonly uploadsDispatch: Dispatch<UploadsAction>;
}

const FileRow = ({ upload, uploadsDispatch }: FileRowProps) => {
  const { key, file, manager, previewUrl, state } = upload;
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [eventFired, setEventFired] = useState(false);
  const uploadState = useUploadState();

  useEffect(() => {
    manager.on('httpUploadProgress', (p) => {
      if (p.total) setProgress((100 * (p.loaded ?? 0)) / p.total);
    });
    manager.done().catch((error: unknown) => console.warn(error));
    return () => {
      if (previewUrl !== undefined) URL.revokeObjectURL(previewUrl);
      void manager.abort();
    };
  }, [manager, previewUrl, setProgress]);

  useEffect(() => {
    if (progress === 100) uploadsDispatch({ type: 'updateState', key, state: 'finished' });
  }, [key, progress, uploadsDispatch]);

  useEffect(() => {
    if (state === 'pending' || eventFired) return;
    setEventFired(true);
    uploadState.listeners.forEach((listener) => listener(file.name, state));
  }, [eventFired, file.name, setEventFired, state, uploadState.listeners]);

  const icon =
    previewUrl !== undefined ? (
      <Box
        alt=""
        component="img"
        src={previewUrl}
        sx={{
          width: 40,
          height: 40,
          objectFit: 'cover',
          borderRadius: 1,
        }}
      />
    ) : file.type === 'application/pdf' ? (
      <PictureAsPdf sx={{ fontSize: 40, color: 'primary.dark' }} />
    ) : PPT_MIME_TYPES.includes(file.type) ? (
      <Pptx sx={{ fontSize: 40, color: 'primary.dark' }} />
    ) : (
      <Movie sx={{ fontSize: 40, color: 'primary.dark' }} />
    );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 2 }}>
      <Box sx={{ flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {file.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
            {formatBytes(file.size)}
          </Typography>
          {state === 'finished' && <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />}
          {state === 'canceled' && (
            <Typography variant="caption" color="text.secondary">
              {t('components.upload_dialog.messages.status_canceled')}
            </Typography>
          )}
        </Box>
        {state === 'pending' && (
          <Progress
            aria-label={t('components.upload_dialog.messages.file_progress', { name: file.name })}
            value={progress}
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
    </Box>
  );
};

// --- Reducer ---

const uploadsReducer = (state: Upload[], action: UploadsAction): Upload[] => {
  if (action.type === 'reset') return [];
  if (action.type === 'add') return [...action.uploads, ...state];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (action.type === 'updateState')
    return state.map((upload) =>
      upload.key === action.key ? { ...upload, state: action.state } : upload,
    );
  return exhausted(action);
};

// --- Main dialog ---

export const UploadDialog = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(UploadDialogDocument, {
    variables: { networkId: currentNetwork.id },
  });
  const rootFolderId = data?.network?.contentFolderTree.id;

  const uploadDispatch = useUploadDispatch();
  const uploadState = useUploadState();

  const currentFolderId = useMemo(
    () =>
      uploadState.folderId
        ? String(uploadState.folderId)
        : rootFolderId
        ? String(rootFolderId)
        : '',
    [rootFolderId, uploadState.folderId],
  );

  const [createUploadConfig] = useMutation(CreateUploadConfigDocument);
  const [uploads, uploadsDispatch] = useReducer(uploadsReducer, []);

  const hasFiles = uploads.length > 0;
  const finishedCount = uploads.filter(({ state }) => state === 'finished').length;
  const totalCount = uploads.length;
  const allDone = hasFiles && uploads.every(({ state }) => state !== 'pending');
  const hasPending = uploads.some(({ state }) => state === 'pending');

  const startUploads = useCallback(
    async (files: readonly File[]) => {
      const { data } = await createUploadConfig({
        variables: { networkId: currentNetwork.id },
      });
      assert(data?.createUploadConfig != null, 'Could not retrieve upload credentials');

      const { bucket, credentials, metadata, region } = data.createUploadConfig;
      const expiration = new Date(credentials.expiration);
      const client = new S3Client({ credentials: { ...credentials, expiration }, region });

      const newUploads = files.map((file) => ({
        key: uuid(),
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        manager: new ManagedUpload({
          client,
          params: {
            Body: file,
            Bucket: bucket,
            ContentType: file.type,
            Key: `uploads/media/${uuid()}`,
            Metadata: {
              ...metadata,
              filename: encodeURIComponent(file.name),
              contentFolderId: currentFolderId,
            },
          },
        }),
        state: 'pending' as const,
      }));

      uploadsDispatch({ type: 'add', uploads: newUploads });
    },
    [createUploadConfig, currentNetwork.id, currentFolderId],
  );

  const onDrop = useCallback(
    async <T extends File>(
      acceptedFiles: T[],
      fileRejections: FileRejection[],
      _event: DropEvent,
    ) => {
      if (fileRejections.length)
        console.warn(
          'Some files could not be uploaded:',
          fileRejections.map((file) => file.errors),
        );
      await startUploads(acceptedFiles);
    },
    [startUploads],
  );

  // Drops land anywhere on the screen; the drop box inside only browses for files.
  const { getRootProps, getInputProps } = useDropzone({
    ...UPLOAD_DROPZONE_OPTIONS,
    noDrag: true,
    onDrop,
  });
  const { getRootProps: getScreenProps } = useDropzone({
    ...UPLOAD_DROPZONE_OPTIONS,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });
  const {
    ref: screenRef,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop: onScreenDrop,
  } = getScreenProps();

  useEffect(() => {
    if (!uploadState.files) return;
    void startUploads(uploadState.files);
    uploadDispatch({ type: 'clearFiles' });
  }, [startUploads, uploadDispatch, uploadState.files]);

  const [confirmClose, confirmCloseProps] = useConfirmDialog();

  const close = useCallback(async () => {
    if (hasPending && !(await confirmClose())) return;
    uploadDispatch({ type: 'setDialogOpen', dialogOpen: false });
  }, [confirmClose, hasPending, uploadDispatch]);

  const onExited = useCallback(() => {
    uploadsDispatch({ type: 'reset' });
    uploadState.returnFocus?.focus();
    // The opener was only needed here; dropping it keeps a detached button out of the context.
    uploadDispatch({ type: 'clearReturnFocus' });
  }, [uploadDispatch, uploadState.returnFocus]);

  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      if (uploads.some(({ state }) => state === 'pending')) {
        event.preventDefault();
        event.returnValue = t('components.upload_dialog.messages.confirm_prompt');
      }
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [t, uploads]);

  // Button label & variant based on state
  const buttonLabel = allDone
    ? t('components.upload_dialog.messages.done')
    : hasPending
    ? t('components.upload_dialog.messages.upload_progress', {
        finished: finishedCount,
        total: totalCount,
      })
    : t('common.dialogs.close');

  const buttonVariant = hasFiles ? 'contained' : 'outlined';

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      disableRestoreFocus
      ref={screenRef as Ref<HTMLDivElement>}
      onClose={close}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onScreenDrop}
      open={uploadState.dialogOpen}
      TransitionProps={{ onExited }}
    >
      <DialogTitle onClose={close}>{t('components.upload_dialog.messages.title')}</DialogTitle>
      <DialogContent sx={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}>
        {/* Drop zone: large when empty, compact strip when files exist */}
        {hasFiles ? (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <input {...getInputProps()} />
            <Add sx={{ fontSize: 28, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              {t('components.upload_dialog.messages.add_more_files')}
            </Typography>
          </Box>
        ) : (
          <Box
            {...getRootProps()}
            sx={{
              alignItems: 'center',
              bgcolor: 'grey.50',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              textAlign: 'center',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <input {...getInputProps()} />
            <DriveFolderUpload sx={{ fontSize: 64, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h5">
              {t('components.upload_dialog.messages.drop_message')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('components.upload_dialog.messages.browse_message')}
            </Typography>
          </Box>
        )}

        {/* File list */}
        {hasFiles && (
          <Box
            sx={{
              mt: 2,
              flex: { xs: 1, sm: 'unset' },
              maxHeight: { sm: 350 },
              overflowY: 'auto',
              '& > :not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
            }}
          >
            {uploads.map((upload) => (
              <FileRow key={upload.key} upload={upload} uploadsDispatch={uploadsDispatch} />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {allDone ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
            <CheckCircle sx={{ fontSize: 18 }} />
            <Typography variant="body1" color="success.main">
              {t('components.upload_dialog.messages.files_added_plural', { count: finishedCount })}
            </Typography>
          </Box>
        ) : (
          <Box />
        )}
        <Button
          color="primary"
          variant={buttonVariant}
          sx={
            hasPending && totalCount > 0
              ? {
                  background: (theme) =>
                    `linear-gradient(to right, ${theme.palette.primary.selected} ${
                      (finishedCount / totalCount) * 100
                    }%, ${theme.palette.primary.dark} ${(finishedCount / totalCount) * 100}%)`,
                  color: 'white',
                }
              : undefined
          }
          onClick={close}
        >
          {buttonLabel}
        </Button>
      </DialogActions>

      <ConfirmDialog
        {...confirmCloseProps}
        title={t('components.upload_dialog.messages.confirm_title')}
        confirm={t('components.upload_dialog.messages.confirm_exit')}
        cancel={t('components.upload_dialog.messages.confirm_continue')}
        prompt={t('components.upload_dialog.messages.confirm_prompt')}
      />
    </Dialog>
  );
};
